import { Box, Menu, SegmentedControl, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { Tabs } from 'src/design-system/Tabs';
import { FamilyMember } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import classes from './FamilyIndividualToggle.module.css';

type FamilyIndividualView = 'family' | 'individual';

type FamilyIndividualToggleVariant = 'list' | 'profile';

type Props = {
  value: FamilyIndividualView;
  onChange: (value: FamilyIndividualView) => void;
  familyLabel?: string;
  individualLabel?: string;
  variant?: FamilyIndividualToggleVariant;
  familyTooltip?: string;
  members?: FamilyMember[];
  onMemberSelect?: (memberId: string) => void;
  familyScreenEnabled?: boolean;
};

export function FamilyIndividualToggle({
  value,
  onChange,
  familyLabel = 'Family',
  individualLabel = 'Individual',
  variant = 'list',
  familyTooltip,
  members,
  onMemberSelect,
  familyScreenEnabled = false,
}: Props) {
  const tabs = [
    { label: familyLabel, value: 'family' as const },
    { label: individualLabel, value: 'individual' as const },
  ];

  if (variant === 'profile') {
    // The reworked profile toggle (custom segments + optional member dropdown)
    // is gated behind flagEnabledFamilyScreenRework. With the flag off, fall
    // back to the original SegmentedControl so the individual-details rework
    // experience stays identical to main.
    if (!familyScreenEnabled) {
      const data = [
        {
          label: familyTooltip ? (
            <Tooltip label={familyTooltip} withArrow>
              <span>{familyLabel}</span>
            </Tooltip>
          ) : (
            familyLabel
          ),
          value: 'family',
        },
        { label: individualLabel, value: 'individual' },
      ];

      return (
        <SegmentedControl
          value={value}
          onChange={v => onChange(v as FamilyIndividualView)}
          data={data}
          classNames={{ label: classes.profileLabel }}
          styles={{
            root: {
              borderRadius: 20,
              padding: 3,
              backgroundColor: 'var(--mantine-color-gray-2)',
            },
            label: {
              paddingLeft: 48,
              paddingRight: 48,
              paddingTop: 10,
              paddingBottom: 10,
              borderRadius: 16,
            },
            indicator: {
              borderRadius: 16,
              backgroundColor: 'var(--mantine-color-gray-0)',
            },
          }}
        />
      );
    }

    return (
      <ProfileToggle
        value={value}
        onChange={onChange}
        familyLabel={familyLabel}
        individualLabel={individualLabel}
        familyTooltip={familyTooltip}
        members={members}
        onMemberSelect={onMemberSelect}
      />
    );
  }

  return <Tabs<FamilyIndividualView> value={value} onChange={onChange} tabs={tabs} />;
}

const profileSegmentBoxStyle = {
  display: 'flex',
  borderRadius: 20,
  padding: 3,
  backgroundColor: 'var(--mantine-color-gray-2)',
  gap: 0,
} as const;

const profileSegmentStyle = {
  paddingLeft: 48,
  paddingRight: 48,
  paddingTop: 10,
  paddingBottom: 10,
  borderRadius: 16,
  whiteSpace: 'nowrap',
} as const;

const profileDropdownSegmentStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  paddingLeft: 48,
  paddingRight: 36,
  paddingTop: 10,
  paddingBottom: 10,
  borderRadius: 16,
  whiteSpace: 'nowrap',
} as const;

type ProfileToggleProps = {
  value: FamilyIndividualView;
  onChange: (value: FamilyIndividualView) => void;
  familyLabel: string;
  individualLabel: string;
  familyTooltip?: string;
  members?: FamilyMember[];
  onMemberSelect?: (memberId: string) => void;
};

// A single stable structure for the profile toggle: the family/individual
// buttons stay mounted regardless of whether the member dropdown is available
// yet. Swapping between separate components (e.g. a SegmentedControl and a
// custom menu) while family data loads would replace the DOM node mid-click and
// drop the first press.
function ProfileToggle({
  value,
  onChange,
  familyLabel,
  familyTooltip,
  individualLabel,
  members,
  onMemberSelect,
}: ProfileToggleProps) {
  const showMemberDropdown = !!(members && members.length > 0 && onMemberSelect);

  return (
    <Box style={profileSegmentBoxStyle}>
      <UnstyledButton
        onClick={() => onChange('family')}
        className={value === 'family' ? classes.profileSegmentActive : classes.profileSegment}
        style={profileSegmentStyle}
      >
        {familyTooltip ? (
          <Tooltip label={familyTooltip} withArrow>
            <span>{familyLabel}</span>
          </Tooltip>
        ) : (
          familyLabel
        )}
      </UnstyledButton>

      {showMemberDropdown ? (
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <UnstyledButton
              className={
                value === 'individual' ? classes.profileSegmentActive : classes.profileSegment
              }
              style={profileDropdownSegmentStyle}
            >
              {individualLabel}
              <IconChevronDown size={20} />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {members.map(member => (
              <Menu.Item key={member.clientId} onClick={() => onMemberSelect(member.clientId)}>
                {formatName(member.client.firstName, member.client.lastName, NameFormat.NATURAL)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      ) : (
        <UnstyledButton
          onClick={() => onChange('individual')}
          className={value === 'individual' ? classes.profileSegmentActive : classes.profileSegment}
          style={profileSegmentStyle}
        >
          {individualLabel}
        </UnstyledButton>
      )}
    </Box>
  );
}
